import { Avatar, Box, Typography } from "@mui/material"
import {
    HandshakeOutlined, PaymentsOutlined, VisibilityOutlined,
    ConstructionOutlined, BlockOutlined, ArrowForward
} from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"
import { FC, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import PageTitle from "../Common/PageTitle"
import ConfirmPinDialog from "../Common/ConfirmPinDialog"
import { Apis } from "../../helpers"
import { EventsEmit } from "../../configs/events"
import { PropertyProps } from "../../types/properties"
import { FoundUser } from "./findUserModal"

interface RoleOption {
    value: string
    label: string
    description: string
    icon: JSX.Element
}

const ASSIGNABLE_ROLES: RoleOption[] = [
    { value: 'Agent', label: 'Agent', description: 'Manage listings, tenants and payments', icon: <HandshakeOutlined /> },
    { value: 'Debt Collector', label: 'Debt Collector', description: 'Collect rent and follow up on arrears', icon: <PaymentsOutlined /> },
    { value: 'Viewer', label: 'Viewer', description: 'View-only access to property data', icon: <VisibilityOutlined /> },
    { value: 'Caretaker', label: 'Caretaker', description: 'Handle maintenance and unit upkeep', icon: <ConstructionOutlined /> },
]
const NONE_ROLE: RoleOption = { value: 'None', label: 'None', description: 'No management permissions', icon: <BlockOutlined /> }

interface LocationState {
    property?: PropertyProps
    collaborator?: FoundUser & { role?: string, status?: string }
    transfer?: boolean
}
interface StateProps {
    selectedRole: string
    showPin: boolean
    submitting: boolean
    pinError: string | null
}

const AddCollaborator: FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { property, collaborator, transfer } = (location.state || {}) as LocationState
    const isEdit = Boolean(!transfer && collaborator?.role)

    const [state, setState] = useState<StateProps>({
        selectedRole: isEdit ? (collaborator?.role || '') : '',
        showPin: false,
        submitting: false,
        pinError: null
    })
    const { selectedRole, showPin, submitting, pinError } = state

    useEffect(() => {
        if (!property || !collaborator) navigate('/collaborators')
        // eslint-disable-next-line
    }, [])

    if (!property || !collaborator) return null

    const roleOptions = transfer ? [...ASSIGNABLE_ROLES.slice(0, 3), NONE_ROLE, ASSIGNABLE_ROLES[3]] : ASSIGNABLE_ROLES

    const submit = (pin: string) => {
        setState(s => ({ ...s, submitting: true, pinError: null }))
        Apis({
            module: 'Properties',
            action: 'addCollaborator',
            collaborator: { ...collaborator, role: transfer ? 'Owner' : selectedRole },
            newUserRole: transfer ? selectedRole : undefined,
            isDirecTransfer: Boolean(transfer),
            isEdit,
            property: { _id: property._id, name: property.name, logo: property.logos?.small?.url },
            pin
        }).then((resp: any) => {
            EventsEmit('app', { info: { message: resp?.message || 'Saved.', status: 'success' } })
            navigate('/collaborators')
        }).catch((err: any) => {
            setState(s => ({
                ...s,
                submitting: false,
                pinError: err?.response?.data?.message || 'Incorrect PIN. Please try again.'
            }))
        })
    }

    const displayName = `${collaborator.firstName || ''} ${collaborator.lastName || ''}`.trim() || collaborator.userName || '—'

    return (
        <Box sx={{ pr: 5, maxWidth: 520 }}>
            <PageTitle backBtn title={transfer ? 'New Owner' : (isEdit ? 'Edit Role' : 'Add Collaborator')} />

            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff', borderRadius: 3, p: 2, mt: 3, mb: 3,
                boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)'
            }}>
                <Avatar src={collaborator.profilePhotos?.small?.url} sx={{ width: 44, height: 44 }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>{displayName}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                        {collaborator.phone || collaborator.email || collaborator.userName || '—'}
                    </Typography>
                </Box>
            </Box>

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>
                {transfer
                    ? "Choose your new role, if any, after the transfer."
                    : 'Choose a role for this collaborator.'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
                {roleOptions.map(role => {
                    const active = selectedRole === role.value
                    return (
                        <Box
                            key={role.value}
                            onClick={() => setState(s => ({ ...s, selectedRole: role.value }))}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.6, borderRadius: 2, cursor: 'pointer',
                                border: '1.5px solid', borderColor: active ? '#8c7c0c' : '#e2e8f0',
                                bgcolor: active ? 'rgba(140,124,12,.06)' : '#fff',
                                transition: 'border-color .15s, background-color .15s'
                            }}
                        >
                            <Box sx={{ color: active ? '#8c7c0c' : '#94a3b8', display: 'flex' }}>{role.icon}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: active ? '#8c7c0c' : '#1e293b' }}>
                                    {role.label}
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                                    {role.description}
                                </Typography>
                            </Box>
                        </Box>
                    )
                })}
            </Box>

            <LoadingButton
                fullWidth
                variant="contained"
                disabled={!selectedRole}
                onClick={() => setState(s => ({ ...s, showPin: true }))}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none', borderRadius: 2, py: 1.5 }}
            >
                Next
            </LoadingButton>

            {showPin && (
                <ConfirmPinDialog
                    title="Enter PIN"
                    message="Complete this operation by entering your PIN."
                    processing={submitting}
                    errorMessage={pinError}
                    onConfirm={submit}
                    close={() => setState(s => ({ ...s, showPin: false, pinError: null }))}
                />
            )}
        </Box>
    )
}

export default AddCollaborator
